import { useState, useEffect, useMemo } from 'react'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Badge from 'react-bootstrap/Badge'
import Form from 'react-bootstrap/Form'
import Card from 'react-bootstrap/Card'
import axios from 'axios'
import _ from 'lodash'
import { createRoot } from 'react-dom/client'
import toast, { Toaster } from 'react-hot-toast'
import Select from 'react-select'
import Row from 'react-bootstrap/esm/Row'
import { VoteForm } from './components/hc_vote_form'
import { parseISO, format } from 'date-fns'

import DelayDeleteButton from './components/delay_delete_button'
import {
  TzAwareDatetimePicker,
  tzAwareNow,
} from './components/tzaware_datetime_picker'
import 'react-datepicker/dist/react-datepicker.css'

const endpoint = '/api/1'

const AdminInterface = ({ updateActiveElections }) => {
  const [hallNameMap, setHallNameMap] = useState([])
  const [elections, setElections] = useState([])
  const [electionOption, setElectionOption] = useState(null)

  const [isCreating, setIsCreating] = useState(false)
  const [description, setDescription] = useState('')
  const [applicationStartDate, setApplicationStartDate] = useState(
    new Date().toISOString().split('T')[0],
  )
  const [applicationEndDate, setApplicationEndDate] = useState(
    new Date().toISOString().split('T')[0],
  )
  const [meetHcDatetime, setMeetHcDatetime] = useState(tzAwareNow())
  const [electionStartDate, setElectionStartDate] = useState(
    new Date().toISOString().split('T')[0],
  )
  const [electionEndDate, setElectionEndDate] = useState(
    new Date().toISOString().split('T')[0],
  )

  const [openHalls, setOpenHalls] = useState([])

  const elections_endpoint = `${endpoint}/hall_councilor/list_elections`
  const modify_endpoint = `${endpoint}/hall_councilor/election`

  /* ============ Helper functions =============== */

  const generateOptions = elections => {
    return _.orderBy(elections, ['election_end_date'], 'desc').map(x => {
      return {
        label: `${x.description} ${
          x.voting_open || x.applications_open ? '' : '(inactive)'
        }`,
        voting_open: x.voting_open,
        applications_open: x.applications_open,
        value: x.election_id,
      }
    })
  }

  /* ============ Handlers =============== */

  const handleCreateElection = () => {
    if (!isCreating) {
      setIsCreating(true)
      setDescription('')
      setApplicationStartDate(new Date().toISOString().split('T')[0])
      setApplicationEndDate(new Date().toISOString().split('T')[0])
      setMeetHcDatetime(tzAwareNow())
      setElectionStartDate(new Date().toISOString().split('T')[0])
      setElectionEndDate(new Date().toISOString().split('T')[0])
      setOpenHalls(hallNameMap.map(h => h.officer_id))
    } else {
      setIsCreating(false)
      updateElectionOption(elections, electionOption)
    }
  }

  const updateElectionOption = (elections, option) => {
    setElectionOption(option)
    const election = _.find(elections, ['election_id', option.value])
    setDescription(election.description)
    setElectionEndDate(election.election_date)
    setApplicationStartDate(election.application_start_date)
    setApplicationEndDate(election.application_end_date)
    setMeetHcDatetime(election.meet_hc_datetime)
    setElectionStartDate(election.election_start_date)
    setElectionEndDate(election.election_end_date)
    setOpenHalls(election.open_halls.map(h => h.officer_id))
  }

  const handleCreateSave = async () => {
    const update_promise = axios.post(modify_endpoint, {
      election_id: isCreating ? null : electionOption.value,
      election_date: electionEndDate,
      description: description,
      application_start_date: applicationStartDate,
      application_end_date: applicationEndDate,
      meet_hc_datetime: meetHcDatetime,
      election_start_date: electionStartDate,
      election_end_date: electionEndDate,
      open_halls: openHalls,
    })

    if (isCreating) {
      toast.promise(update_promise, {
        loading: 'Creating election...',
        success: 'Created election!',
        error: 'Failed to create election!',
      })
    } else {
      toast.promise(update_promise, {
        loading: 'Updating election...',
        success: 'Updated election!',
        error: 'Failed to update election!',
      })
    }

    try {
      const { data } = await update_promise
      setIsCreating(false)
      const newElections = [
        ..._.filter(elections, e => e.election_id !== data.election_id),
        data,
      ]
      const newOption = generateOptions([data])[0]
      setElections(newElections)
      updateElectionOption(newElections, newOption)
      updateActiveElections()
    } catch (e) {
      console.error(e)
    }
  }

  const handleDelete = async () => {
    const data_promise = axios.delete(modify_endpoint, {
      params: { election_id: electionOption.value },
    })

    toast.promise(data_promise, {
      loading: 'Deleting election...',
      success: 'Deleted election!',
      error: 'Failed to delete election!',
    })

    try {
      await data_promise
      const newElections = [
        ..._.filter(elections, e => e.election_id !== electionOption.value),
      ]
      const newOption = generateOptions([newElections.at(-1)])[0]
      setElections(newElections)
      updateElectionOption(newElections, newOption)
      updateActiveElections()
    } catch (e) {
      console.error(e)
    }
  }

  const toggleOpenHall = officer_id => {
    if (openHalls.includes(officer_id)) {
      setOpenHalls(openHalls.filter(h => h != officer_id))
    } else {
      setOpenHalls([officer_id, ...openHalls])
    }
  }

  /* ============ Effects =============== */

  // get the election list
  useEffect(() => {
    const setData = async () => {
      const elections_promise = axios.get(elections_endpoint, {
        params: { active_only: false },
      })

      try {
        const { data: election_data } = await elections_promise
        setHallNameMap(election_data.hall_names)
        setElections(election_data.elections)
        updateElectionOption(
          election_data.elections,
          generateOptions(election_data.elections)[0],
        )
      } catch (e) {
        toast.error('Unable to get list of elections!')
        console.error(e)
      }
    }

    setData()
  }, [])

  /* ============ Rendering =============== */

  const election = useMemo(
    () =>
      electionOption === null
        ? null
        : _.find(elections, ['election_id', electionOption.value]),
    [elections, electionOption],
  )

  const needsSave =
    election !== null &&
    (description != election.description ||
      electionStartDate != election.election_start_date ||
      electionEndDate != election.election_end_date ||
      meetHcDatetime != election.meet_hc_datetime ||
      applicationStartDate != election.application_start_date ||
      applicationEndDate != election.application_end_date ||
      _.xor(
        openHalls,
        election.open_halls.map(h => h.officer_id),
      ).length != 0)

  const selectOptions = generateOptions(elections)
  if (isCreating) {
    selectOptions.unshift({ label: 'New election', value: null })
  }

  const style_preicons = data => ({
    alignItems: 'center',
    display: 'flex',

    ':after': {
      // You need nested quotes because the outer quotes gets "stripped"
      // going into the CSS
      content: `"${data.voting_open ? '\\f772' : ''}   ${
        data.applications_open ? '\\f4ff' : ''
      }"`,
      fontFamily: '"Font Awesome 5 Free"',
      fontWeight: 900,
      display: 'block',
      whiteSpace: 'pre',
      marginLeft: 8,
    },
  })
  const selectStyles = {
    option: (styles, { data }) => {
      return {
        ...styles,
        color: data.voting_open || data.applications_open ? 'black' : '#aaa',
        ...style_preicons(data),
      }
    },
    singleValue: (styles, { data }) => ({
      ...styles,
      ...style_preicons(data),
    }),
  }

  return (
    <Card className="mb-3">
      <Card.Body>
        <Card.Title>Admin interface</Card.Title>
        <Form>
          <Form.Group controlId="electionSelect">
            <Form.Label>Select election</Form.Label>
            <Row className="mb-3 gx-5">
              <Select
                className="col-md-6"
                options={selectOptions}
                styles={selectStyles}
                value={isCreating ? selectOptions[0] : electionOption}
                isDisabled={isCreating}
                onChange={option => updateElectionOption(elections, option)}
              />
              <Button
                className="col-md-3 ms-4 me-auto"
                style={{ width: '12em' }}
                onClick={handleCreateElection}
              >
                {isCreating ? 'Cancel' : 'Create new election'}
              </Button>
            </Row>
          </Form.Group>
          {electionOption !== null && (
            <>
              <hr />
              <Form.Group className="mb-2" controlId="description">
                <Form.Label>Election name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter election name"
                  value={description}
                  onChange={e => {
                    setDescription(e.target.value)
                  }}
                ></Form.Control>
              </Form.Group>
              <div className="mb-2 d-flex" style={{ gap: '10px' }}>
                {election.applications_open && (
                  <Badge bg="primary">Applications open</Badge>
                )}
                {election.voting_open && (
                  <Badge bg="primary">Voting open</Badge>
                )}
              </div>
              <Row className="mb-3 gy-4">
                <Form.Group
                  className="col-md-2"
                  style={{ minWidth: '13em' }}
                  controlId="date"
                >
                  <Form.Label>Application start date</Form.Label>
                  <Form.Control
                    class="form-control"
                    type="date"
                    value={applicationStartDate}
                    onChange={e => {
                      setApplicationStartDate(e.target.value)
                    }}
                  />
                  <Form.Text className="text-muted">
                    Specify the application start date. Applications will open
                    at 12:00:00 am on this day.
                  </Form.Text>
                </Form.Group>
                <Form.Group
                  className="col-md-2"
                  style={{ minWidth: '13em' }}
                  controlId="date"
                >
                  <Form.Label>Application end date</Form.Label>
                  <Form.Control
                    class="form-control"
                    type="date"
                    value={applicationEndDate}
                    onChange={e => {
                      setApplicationEndDate(e.target.value)
                    }}
                  />
                  <Form.Text className="text-muted">
                    Specify the application end date. Applications will close at
                    11:59:59 pm on this day.
                  </Form.Text>
                </Form.Group>
                <Form.Group
                  className="col-md-2 mx-auto"
                  style={{ minWidth: '20em' }}
                >
                  <Form.Label>Meet the HC event</Form.Label>
                  <TzAwareDatetimePicker
                    hideDistance
                    innerStyle={{
                      padding: '6px 12px 6px 12px',
                    }}
                    id="meet_hc_dt"
                    val={meetHcDatetime}
                    setVal={setMeetHcDatetime}
                    restrictTo15={true}
                  />
                  <Form.Text className="text-muted">
                    Specify the date and time of the "Meet the HC" event that
                    candidates are expected to attend.
                  </Form.Text>
                </Form.Group>
                <Form.Group
                  className="col-md-2"
                  style={{ minWidth: '13em' }}
                  controlId="date"
                >
                  <Form.Label>Election start date</Form.Label>
                  <Form.Control
                    class="form-control"
                    type="date"
                    value={electionStartDate}
                    onChange={e => {
                      setElectionStartDate(e.target.value)
                    }}
                  />
                  <Form.Text className="text-muted">
                    Specify the voting start date. The election will open at
                    12:00:00 am on this day.
                  </Form.Text>
                </Form.Group>
                <Form.Group
                  className="col-md-2"
                  style={{ minWidth: '13em' }}
                  controlId="date"
                >
                  <Form.Label>Election end date</Form.Label>
                  <Form.Control
                    class="form-control"
                    type="date"
                    value={electionEndDate}
                    onChange={e => {
                      setElectionEndDate(e.target.value)
                    }}
                  />
                  <Form.Text className="text-muted">
                    Specify the voting end date. The election will close at
                    11:59:59 pm on this day.
                  </Form.Text>
                </Form.Group>
              </Row>
              <h4 className="mt-3">Open halls</h4>
              <Row className="px-3 mb-3">
                {_.sortBy(hallNameMap, 'officer_name').map(hall => (
                  <Form.Check
                    id={`open-hall-admin--${hall.officer_id}`}
                    style={{ maxWidth: '15em' }}
                    inline
                    label={hall.officer_name}
                    type="checkbox"
                    checked={openHalls.includes(hall.officer_id)}
                    onChange={() => toggleOpenHall(hall.officer_id)}
                  />
                ))}
              </Row>
              <div className="d-flex">
                <Button
                  variant="primary"
                  onClick={handleCreateSave}
                  disabled={!isCreating && !needsSave}
                >
                  {isCreating ? 'Create' : 'Save'}
                </Button>
                {isCreating || (
                  <DelayDeleteButton
                    className="ms-auto me-2"
                    onDelete={handleDelete}
                    delayTimeMs={1000}
                  />
                )}
              </div>
            </>
          )}
        </Form>
      </Card.Body>
    </Card>
  )
}

const candidate_img_style = {
  width: '20em',
  margin: 'auto',
  padding: '1em',
}

const PlanRadioButtons = ({ label, id, value, onChange }) => {
  const targetOnChange = e => onChange(e.currentTarget.value)
  return (
    <Form.Group>
      <Form.Label className="col-sm-8">{label}</Form.Label>
      <Form.Check
        inline
        type="radio"
        name={id}
        id={`${id}-yes`}
        value="yes"
        label="yes"
        checked={value === 'yes'}
        onChange={targetOnChange}
      />
      <Form.Check
        inline
        type="radio"
        name={id}
        id={`${id}-no`}
        value="no"
        label="no"
        checked={value === 'no'}
        onChange={targetOnChange}
      />
      <Form.Check
        inline
        type="radio"
        name={id}
        id={`${id}-unsure`}
        value="unsure"
        label="unsure"
        checked={value === 'unsure'}
        onChange={targetOnChange}
      />
    </Form.Group>
  )
}

const ApplicationModal = ({
  show,
  onClose,
  onSubmit,
  application_election,
}) => {
  const [availHouseMeeting, setAvailHouseMeeting] = useState('yes')
  const [availBrunch, setAvailBrunch] = useState('yes')
  const [availRetreat, setAvailRetreat] = useState('yes')
  const [availSummer, setAvailSummer] = useState('yes')
  const [availOrientation, setAvailOrientation] = useState('yes')
  const [summerPlans, setSummerPlans] = useState('')
  const [applicationReason, setApplicationReason] = useState('')
  const [eventPlan, setEventPlan] = useState('')
  const [previousExperience, setPreviousExperience] = useState('')
  const [currentInvolvement, setCurrentInvolvement] = useState('')
  const [msgs, setMsgs] = useState({})

  const [acceptResponsibilities, setAcceptResponsibilities] = useState(false)
  const [acceptCampaignPolicy, setAcceptCampaignPolicy] = useState(false)
  const [confirmingWithdraw, setConfirmingWithdraw] = useState(false)
  const [withdraw, setWithdraw] = useState(false)

  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  // Reset the withdraw state whenever the modal is re-shown
  const [lastShow, setLastShow] = useState(show)
  if (show !== lastShow) {
    setLastShow(show)
    setConfirmingWithdraw(false)
    setWithdraw(false)
  }

  useEffect(() => {
    const fetchApplication = async () => {
      try {
        const { data } = await axios.get(
          `${endpoint}/hall_councilor/application`,
          {
            params: {
              election_id: application_election.election_id,
            },
          },
        )
        setAvailHouseMeeting(data.house_meeting)
        setAvailBrunch(data.brunch)
        setAvailRetreat(data.retreat)
        setAvailSummer(data.most_of_summer)
        setAvailOrientation(data.orientation)
        setSummerPlans(data.summer_plans)
        setApplicationReason(data.reasons)
        setEventPlan(data.plans)
        setPreviousExperience(data.experience)
        setCurrentInvolvement(data.involvement)
        setMsgs(data.apply_to_halls)
        setAcceptResponsibilities(true)
        setAcceptCampaignPolicy(true)
        setWithdraw(data.withdrawn)
      } catch (e) {
        console.error(e)
      }
    }

    fetchApplication()
  }, [])

  const ID_TO_NAME = {}
  application_election.open_halls.forEach(open_hall => {
    ID_TO_NAME[open_hall.officer_id] = open_hall.officer_name.replace(
      'Hall Councilor',
      '',
    )
  })

  const handleApplyToHalls = officer_id => {
    if (officer_id in msgs) {
      let newMsgs = { ...msgs }
      delete newMsgs[officer_id]
      setMsgs(newMsgs)
    } else {
      let newMsgs = { ...msgs }
      newMsgs[officer_id] = ''
      setMsgs(newMsgs)
    }
  }

  function hall_compare(hall_a, hall_b) {
    // Sort halls by officer name
    return hall_a.officer_name > hall_b.officer_name
  }

  const open_halls_grid_style = {
    display: 'flex',
    'flex-wrap': 'wrap',
    'justify-content': 'space-between',
  }
  const open_halls_check_style = {
    flex: '0 0 25%',
    'margin-bottom': '0.5 rem',
  }

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {`${application_election.description}`} application
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>
              You currently have this "My Account" photo: (
              <a href="/secure/profile.php">
                change photo on your profile page
              </a>
              )
            </Form.Label>
            <br />
            <div className="d-flex">
              <img
                src={`${endpoint}/residents/self_image`}
                style={candidate_img_style}
              />
            </div>
          </Form.Group>

          <Form.Group>
            <Form.Label>What are your current summer plans?</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={summerPlans}
              onChange={e => setSummerPlans(e.target.value)}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>What are your reasons for applying?</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={applicationReason}
              onChange={e => setApplicationReason(e.target.value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>
              What are your plans for organizing events and encouraging resident
              participation?
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={eventPlan}
              onChange={e => setEventPlan(e.target.value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>
              What relevant previous experience do you have for this role?
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={previousExperience}
              onChange={e => setPreviousExperience(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-4">
            <Form.Label>
              What is your current involvement with SP? Do you attend events?
              Have you interacted with officers? Are you a helper?
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={currentInvolvement}
              onChange={e => setCurrentInvolvement(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-5">
            <Form.Label style={{ fontWeight: 'bold' }}>
              Will you be attending the following events?
            </Form.Label>
            <PlanRadioButtons
              label="the late April / early May transition house meeting"
              id="transition_hm"
              value={availHouseMeeting}
              onChange={setAvailHouseMeeting}
            />
            <PlanRadioButtons
              label="the late April / early May transition brunch"
              id="transition_brunch"
              value={availBrunch}
              onChange={setAvailBrunch}
            />
            <PlanRadioButtons
              label="the late May officer retreat"
              id="officer_retreat"
              value={availRetreat}
              onChange={setAvailRetreat}
            />
            <PlanRadioButtons
              label="Will you be here for most of the summer?"
              id="during_summer"
              value={availSummer}
              onChange={setAvailSummer}
            />
            <PlanRadioButtons
              label="Will you be here during orientation (late August to mid September)?"
              id="during_orientation"
              value={availOrientation}
              onChange={setAvailOrientation}
            />
          </Form.Group>
          <Form.Group className="mb-4">
            <Form.Label style={{ fontWeight: 'bold' }}>Open halls</Form.Label>
            <br />
            <div style={open_halls_grid_style}>
              {application_election.open_halls
                .sort(hall_compare)
                .map((open_hall, idx) => (
                  <Form.Check
                    id={`open-hall-checkbox-${idx}`}
                    inline
                    label={open_hall.officer_name}
                    type="checkbox"
                    checked={open_hall.officer_id in msgs}
                    onChange={() => handleApplyToHalls(open_hall.officer_id)}
                    style={open_halls_check_style}
                  />
                ))}
            </div>
            <br />
            {Object.keys(msgs).map(officer_id => (
              <>
                <Form.Label>
                  Message specific to {ID_TO_NAME[officer_id]}
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={msgs[officer_id]}
                  onChange={e => {
                    let newMsgs = { ...msgs }
                    newMsgs[officer_id] = e.target.value
                    setMsgs(newMsgs)
                  }}
                />
              </>
            ))}
          </Form.Group>
          <hr />

          <Form.Group className="mb-3">
            <h3>Expectations</h3>
            <p>
              Typically, hall councilors organize activities (independently or
              collaboratively) every four weeks. Hall councilors are expected to
              organize a range of activities to encourage resident
              participation, and are encouraged to collaborate with other hall
              councilors and other officers to broaden the scope of events that
              can be held.
            </p>
            <p>
              It is Sidney Pacific policy that if an officer fails to fulfill
              his or her responsibilities, he or she will lose Sidney Pacific
              housing privileges.
            </p>
            <Form.Check
              label="I understand and agree to the above"
              type="checkbox"
              id="responsibilitiesAccept"
              checked={acceptResponsibilities}
              onChange={() => {
                setAcceptResponsibilities(!acceptResponsibilities)
              }}
            />
          </Form.Group>

          <Form.Group>
            <h3>Campaigning policy</h3>
            <p>
              Campaigning for Hall Councilor positions is governed by the bylaws
              (Bylaw Amendment Jan 22 2019):
            </p>
            <ol>
              <li>
                Candidates may send campaign-related emails to halls for which
                they are running via the appropriate mailing list.
              </li>
              <li>
                Candidates are not allowed to spend their own, individual money
                on any products or services related to a campaign or distribute
                any products or services as promotional materials to residents
                of halls for which they are running.
              </li>
              <li>
                However, candidates are allowed to use up to $30 - $40
                (depending on the number of HC applicants) of SP funding set by
                SPEC (via the established spending and reimbursement process) to
                host events for the residents of hallways in order to meet
                residents and promote their candidacy. Note that this is not
                required of candidates.
              </li>
              <li>
                Candidates may promote their candidacy by talking to individual
                residents.
              </li>
              <li>
                Candidates cannot receive public endorsements from members of
                the SP Hall Council, Executive Council, or Trustees.
              </li>
            </ol>
            <p>
              Contact{' '}
              <a href="mailto:sp-trustees@mit.edu">sp-trustees@mit.edu</a> if
              you have any questions.
            </p>
            <Form.Check
              label="I understand and agree to the above"
              id="campaignPolicyAccept"
              type="checkbox"
              checked={acceptCampaignPolicy}
              onChange={() => {
                setAcceptCampaignPolicy(!acceptCampaignPolicy)
              }}
            />
            {confirmingWithdraw && (
              <>
                <hr />
                <Form.Check
                  label="I understand and agree to WITHDRAW my application. Note this action is IRREVERSIBLE."
                  type="checkbox"
                  id="withdrawConfirm"
                  checked={withdraw}
                  onChange={() => {
                    setWithdraw(!withdraw)
                  }}
                />
              </>
            )}
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            const payload = {
              house_meeting: availHouseMeeting,
              brunch: availBrunch,
              retreat: availRetreat,
              most_of_summer: availSummer,
              orientation: availOrientation,
              summer_plans: summerPlans,
              reasons: applicationReason,
              plans: eventPlan,
              experience: previousExperience,
              involvement: currentInvolvement,
              apply_to_halls: msgs,
              election_id: application_election.election_id,
            }
            onSubmit(payload)
          }}
          disabled={!(acceptResponsibilities && acceptCampaignPolicy)}
        >
          Submit
        </Button>
        <Button
          variant="danger"
          onClick={() => {
            if (!confirmingWithdraw) {
              setConfirmingWithdraw(true)
            } else {
              onSubmit({
                withdrawn: true,
                election_id: application_election.election_id,
              })
            }
          }}
          disabled={confirmingWithdraw && !withdraw}
        >
          {confirmingWithdraw ? 'Confirm withdraw' : 'Withdraw'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

const ApplicationInterface = ({
  application_elections,
  electionStatus,
  updateActiveElections,
}) => {
  const [showModal, setShowModal] = useState(
    Array(application_elections.length).fill(false),
  )

  const onClick = modal_idx => {
    let showModalNew = [...showModal]
    showModalNew[modal_idx] = !showModalNew[modal_idx]
    setShowModal(showModalNew)
  }

  let buttonStyles = { ...electionStatus }
  Object.keys(buttonStyles).forEach(s => {
    if (buttonStyles[s].includes('Apply')) {
      buttonStyles[s] = 'primary'
    } else if (buttonStyles[s].includes('Edit')) {
      buttonStyles[s] = 'success'
    } else {
      buttonStyles[s] = 'danger'
    }
  })

  const onClickSubmit = async (modal_idx, payload) => {
    if ('withdrawn' in payload) {
      // call the withdraw endpoint with no value
      await axios.post(`${endpoint}/hall_councilor/withdraw_application`, {
        election_id: payload.election_id,
      })
      updateActiveElections()
    } else {
      // call the update endpoint with the payload
      await axios.post(`${endpoint}/hall_councilor/application`, payload)
      updateActiveElections()
    }

    onClick(modal_idx)
  }

  return (
    <Card className="mb-3">
      <Card.Body>
        <Card.Title>Apply to be a Hall Councilor</Card.Title>
        {application_elections.length > 0 && (
          <Form>
            <Form.Group>
              <h3>Introduction</h3>
              <p>
                The Hall Councilors serve as the first contact point between
                residents and the greater Sidney-Pacific community. They are
                energetic, outgoing, and self-motivated individuals who are
                committed to facilitating the residential program. Hall
                Councilors are blessed with the freedom to hold monthly events,
                of their own design, to bring together members of the
                Sidney-Pacific community. Simple examples range from holding
                study groups or game nights in the common area with snacks, to
                playing sports and participating on trips together. Unlike other
                officers, Hall Councilors are generally not constrained by event
                type. As such, the Hall Councilors play an important role in
                looking after the residents, making sure residents feel welcome
                during orientation and part of the Sidney Pacific community
                during the year.
              </p>
              <p>
                On a house government level, the responsibilities of Hall
                Councilors include "representing the interests of their
                respective Halls to the House Council." As the only members of
                the House Council directly elected by the residents, Hall
                Councilors have an important role in house policymaking; this is
                reflected in the fact that their votes are weighted twice as
                much as those of other officers. Hall Councilors are expected to
                attend and participate in monthly House Council meetings and
                Hall Councilor meetings.
              </p>
              <p>
                <i>
                  In addition, Hall Councilors are responsible for "organizing
                  activities and encouraging interactions among the residents at
                  the Hall level" (SP Constitution).
                </i>
              </p>
              <p>
                To summarize, the responsibilities of Hall Councilors include:
                <ul>
                  <li>Organizing activities</li>
                  <li>
                    Representing the interests of their respective Halls to the
                    House Council, and
                  </li>
                  <li>
                    Encouraging interactions among the residents at the Hall
                    level.
                  </li>
                </ul>
              </p>
              <p>
                Hall Councilors have a termly budget to organize hall activities
                or procure hall resources. They have a lot of flexibility to
                develop programs which foster a greater sense of community
                within the halls.
              </p>
              <p>
                Hall Councilors are expected to attend and participate in
                monthly House Council meetings, and Hall Councilor meetings,
                house events, and other activities.
              </p>
              <p>
                Please note that a substantial amount of social events,
                relationship-building with other officers and residents, and
                learning about the Sidney Pacific government structure occurs in
                the summer. Although residents are not required to be present at
                Sidney Pacific during the summer to run for Hall Councilor, it
                is strongly recommended that Hall Councilor applicants be living
                in Sidney Pacific during the summer.
              </p>

              <h3>General Procedures</h3>
              <ul>
                <li>
                  Any current resident of Sidney Pacific may apply for a hall
                  councilor position in any open hall.
                </li>
                <li>
                  All residents will vote for the candidates for their current
                  halls.
                </li>
                <li>
                  If you are also applying for other officer positions, you will
                  receive interviews for those positions separately.
                </li>
                <li>
                  Hall Councilors will be expected to attend the May House
                  Council meeting in late April / early May, the officers'
                  transition brunch in late April / early May, and the officers'
                  retreat in mid May.
                </li>
                <li>
                  Hall Councilors are not required to live in their respective
                  Halls.
                </li>
              </ul>
            </Form.Group>
          </Form>
        )}
        <h3>Applications</h3>
        {application_elections.length == 0 && (
          <Card.Subtitle className="mb-2 text-muted">
            There are no applications open right now
          </Card.Subtitle>
        )}
        {application_elections.map((application_election, idx) => (
          <div key={`application-election-modal-${idx}`} className="mb-3">
            <h4>{application_election.description}</h4>
            <ul>
              <li>
                Applications are open until{' '}
                <b>
                  {format(
                    parseISO(application_election.application_end_date),
                    'MMMM dd',
                  )}{' '}
                  11:59pm
                </b>
                . However, we strongly encourage you to submit your applications
                as early as possible so that residents have a chance to read
                your statement.
              </li>
              <li>
                Meet the Hall Councilors events will be held on{' '}
                <b>
                  {format(
                    parseISO(application_election.meet_hc_datetime),
                    "MMMM dd 'at' HH:mm",
                  )}
                </b>
                . This is an opportunity for residents and potential candidates
                to meet the current Hall Councilors and learn more about the
                role.
              </li>
              <li>
                Voting begins on{' '}
                <b>
                  {format(
                    parseISO(application_election.election_start_date),
                    'MMMM dd',
                  )}
                </b>
                . Voting ends on{' '}
                <b>
                  {format(
                    parseISO(application_election.election_end_date),
                    'MMMM dd',
                  )}
                  , 00:00:00.
                </b>
              </li>
            </ul>
            {Object.keys(electionStatus).length >= 0 && (
              <Button
                variant={buttonStyles[application_election.election_id]}
                disabled={
                  buttonStyles[application_election.election_id] === 'danger'
                }
                onClick={() => onClick(idx)}
              >
                {electionStatus[application_election.election_id]}
              </Button>
            )}
            <ApplicationModal
              show={showModal[idx]}
              onClose={() => onClick(idx)}
              onSubmit={payload => onClickSubmit(idx, payload)}
              application_election={application_election}
            />
          </div>
        ))}
      </Card.Body>
    </Card>
  )
}

const VotingInterface = ({ voting_elections, rng_seed, voting_for_hall }) => {
  const vote_components = []

  for (const election of voting_elections) {
    if (!election.election.voting_open) continue

    const min_candidates = 5 // TODO: parametrize
    const election_id = election.election.election_id
    const candidates = election.candidates
    vote_components.push(
      <div>
        <h4>{election.election.description}</h4>
        <VoteForm
          candidates={candidates}
          min_candidates={min_candidates}
          rng_seed={rng_seed}
          election_id={election_id}
          has_voted={election.has_voted}
        />
      </div>,
    )
  }

  return (
    <Card className="mb-3">
      <Card.Body>
        <Card.Title>Vote for {voting_for_hall.officer_name}</Card.Title>
        {voting_elections.length == 0 && (
          <Card.Subtitle className="mb-2 text-muted">
            There are no hall councilor votes open right now
          </Card.Subtitle>
        )}
        {voting_elections.length > 0 && (
          <>
            <h3>Introduction</h3>
            <p>
              Residents have the opportunity to contribute to the Sidney Pacific
              community by electing Hall Councilors. Please carefully consider
              the responsibilities of the hall councilor as you read the
              candidate statements and place your vote.
            </p>
            <p>
              The Hall Councilors serve as the first contact point between the
              residents and the greater Sidney Pacific community. They are
              energetic, outgoing, and self-motivated individuals who are
              committed to facilitating the residential program. The
              responsibilities of Hall Councilors include "representing the
              interests of their respective Halls to the House Council" (SP
              Constitution). As the only members of the House Council directly
              elected by the residents, the Hall Councilors have an important
              role in house policymaking; this is reflected in the fact that
              their votes are weighted twice as much as those of other officers.
              Hall Councilors are expected to attend and participate in monthly
              House Council meetings and Hall Councilor meetings beginning in
              May.
            </p>
            <p>
              In addition, Hall Councilors are responsible for "organizing
              activities and encouraging interactions among the residents at the
              Hall level" (SP Constitution). The Hall Councilors play an
              important role in looking after the residents, making sure
              residents feel welcome during orientation and part of the Sidney
              Pacific community during the year. Hall Councilors will have a
              termly budget to organize hall activities or procure hall
              resources. They have a lot of flexibility to develop programs
              which foster a greater sense of community within the halls. Please
              note, a lot of social events, relationship-building with other
              officers and residents, and learning about the Sidney Pacific
              government structure occurs in the summer. Although residents are
              not required to be present at Sidney Pacific during the summer to
              run for Hall Councilor, the strongest and most active hall
              councilors have historically been those living at Sidney Pacific
              during the summer.
            </p>

            <h3>About elections</h3>
            <p>
              Voting is anonymous. After you submit your vote, a random code
              will be generated and saved with your vote in the database. A copy
              of this code as well as a record of your vote will be emailed to
              you at the address specified below. Please save this email, as it
              will provide a way of verifying votes if necessary, while keeping
              the process anonymous. You may change your vote once you have
              submitted it only if you provide the passcode provided to you when
              you first voted.
            </p>

            <h3>Information from the Sidney-Pacific Bylaws</h3>
            <p>
              Voters will be asked to rank a minimum of five candidates (or all
              candidates if their number is smaller than five) in order of
              preference and the votes will be counted using the Partial
              Preferential method. If no candidate receives a majority of first
              choice votes, the candidate with the fewest first place votes will
              be eliminated and that candidate's votes will go to the candidate
              that was listed as a second choice. This process will continue
              until a candidate receives a majority of the votes and is declared
              the winner. The first runner-up is determined by eliminating the
              winner from the votes cast and applying the partial preferential
              method as described above among the remaining candidates. This
              process is repeated until a complete order of finish for all
              candidates is determined. The winning candidate in any election
              shall be granted the option to accept office. If the option is not
              exercised within a time period specified by the trustees (not less
              than three days), it shall pass to the runners-up, in order of
              finish, resting with each for a period of one day, until the
              office is accepted.
            </p>

            <h3>Voting</h3>
            {vote_components}
          </>
        )}
      </Card.Body>
    </Card>
  )
}

const HcElections = ({ isAdmin, isTrustee, rng_seed }) => {
  const [elections, setElections] = useState(null)
  const [electionStatus, setElectionStatus] = useState({})

  const election_endpoint = `${endpoint}/hall_councilor/active_elections`

  const updateActiveElections = async () => {
    const election_promise = axios.get(election_endpoint)

    try {
      const { data: election_data } = await election_promise
      setElections(election_data)

      const app_reqs = election_data.apply_elections.map(application_election =>
        axios
          .get(`${endpoint}/hall_councilor/application`, {
            params: {
              election_id: application_election.election_id,
            },
          })
          .catch(e =>
            Promise.resolve({
              election_id: application_election.election_id,
              status: 'Apply',
            }),
          ),
      )

      let raw_data = await Promise.all(app_reqs)

      let newApplications = {}

      raw_data.forEach(_raw_data => {
        if (_raw_data.status === 'Apply') {
          newApplications[_raw_data.election_id] = _raw_data.status
        } else {
          if (_raw_data.data.withdrawn) {
            newApplications[_raw_data.data.election_id] =
              'You application was withdrawn. Please contact Chair of the Halls to re-open your application.'
          } else {
            newApplications[_raw_data.data.election_id] =
              'Edit your application'
          }
        }
      })

      setElectionStatus(newApplications)
    } catch (e) {
      toast.error('Unable to get list of elections details!')
      console.error(e)
    }
  }

  // get the election list
  useEffect(() => {
    updateActiveElections()
  }, [])

  return (
    <>
      {isAdmin && (
        <AdminInterface updateActiveElections={updateActiveElections} />
      )}
      <VotingInterface
        voting_elections={elections !== null ? elections.vote_elections : []}
        rng_seed={rng_seed}
        voting_for_hall={
          elections !== null ? elections.voting_for : 'Hall Councilors'
        }
      />
      <ApplicationInterface
        application_elections={
          elections !== null ? elections.apply_elections : []
        }
        electionStatus={electionStatus}
        updateActiveElections={updateActiveElections}
      />
      <div>
        <Toaster />
      </div>
    </>
  )
}

export default HcElections

const container = document.getElementById('app')
const is_admin = container.dataset.hasAdminPermission === 'true'
const is_trustee = container.dataset.hasTrusteePermission === 'true'
const rng_seed = container.dataset.rngSeed
const root = createRoot(container)
root.render(
  <HcElections isAdmin={is_admin} isTrustee={is_trustee} rng_seed={rng_seed} />,
)
